/**
 * V11.4 — Cockpit Fédération.
 *
 * Route : /mes-federations/:slug
 *
 * Layout : header fédé + KPI + onglets (Dashboard / Athletes / Clubs / Coachs /
 * Résultats / Articles / Livrets & Mémoires).
 *
 * Toutes les données sont récupérées via /api/federation-cockpit/{slug}/*
 * qui applique un contrôle stricte de rôle (owner_user_id, EntityManager
 * approved ou staff admin/copf).
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Building2, Users, Trophy, Flag, Newspaper, BookOpen, Medal, Crown, Settings,
  Award, Target, TrendingUp, ArrowLeft, ExternalLink, Loader2, Search,
  CheckCircle2, XCircle, UserCheck, AlertTriangle, MessageSquareWarning, RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { showError } from "@/lib/errors";
import { useAuth } from "@/lib/auth";
import { resolveAthletePhoto as photoFallback } from "@/lib/photoFallback";
import { ExportXlsxButton } from "@/components/ExportXlsxButton";

function KpiCard({ label, value, icon: Icon, tone = "neutral", subtext = null, testid }) {
  const toneClass = {
    gold: "bg-[hsl(var(--aito-gold))]/10 border-[hsl(var(--aito-gold))]/30 text-[hsl(var(--aito-gold))]",
    lagoon: "bg-[hsl(var(--aito-lagoon))]/10 border-[hsl(var(--aito-lagoon))]/30 text-[hsl(var(--aito-lagoon))]",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    neutral: "bg-background",
  }[tone];
  return (
    <Card className={`p-4 flex items-start gap-3 ${toneClass}`} data-testid={testid}>
      {Icon && <Icon className="h-5 w-5 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <div className="text-2xl font-bold font-display leading-tight">{value ?? "—"}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
        {subtext && <div className="text-[10px] text-muted-foreground mt-0.5">{subtext}</div>}
      </div>
    </Card>
  );
}

export default function FederationCockpitPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState(null); // { federation, kpis }
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/connexion"); return; }
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/federation-cockpit/${slug}/kpis`);
        setData(d);
      } catch (e) {
        if (e?.response?.status === 403) setAccessDenied(true);
        else if (e?.response?.status === 404) navigate("/federations");
        else showError(e, { prefix: "Cockpit :" });
      } finally { setLoading(false); }
    })();
  }, [slug, authLoading, user, navigate]);

  if (authLoading || loading) {
    return <div className="max-w-7xl mx-auto p-6 space-y-4"><Skeleton className="h-32 w-full" /><Skeleton className="h-96 w-full" /></div>;
  }
  if (accessDenied) {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="fed-cockpit-forbidden">
        <Card className="p-6 border-destructive/40 bg-destructive/5">
          <h1 className="font-display text-xl font-bold mb-2">Accès refusé</h1>
          <p className="text-sm text-muted-foreground mb-3">
            Tu n'es pas gestionnaire de cette Fédération. Si tu penses que c'est une erreur, contacte l'administrateur AITO.PF ou fais une demande via « Je gère cette page » sur la fiche publique.
          </p>
          <Link to={`/federations/${slug}`}><Button variant="outline"><ExternalLink className="h-4 w-4 mr-1.5" />Voir la fiche publique</Button></Link>
        </Card>
      </div>
    );
  }
  if (!data) return null;

  const { federation: fed, kpis } = data;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-5" data-testid="fed-cockpit-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Link to="/espace"><Button size="sm" variant="ghost" data-testid="fed-cockpit-back"><ArrowLeft className="h-4 w-4 mr-1" /></Button></Link>
          <div className="h-16 w-16 rounded-xl bg-muted flex items-center justify-center overflow-hidden shrink-0 border">
            {fed.logo_url ? (
              <img src={fed.logo_url} alt={fed.name} className="object-cover w-full h-full" onError={(e) => { e.currentTarget.src = "/branding/cover-federation-default.png"; }} />
            ) : <Crown className="h-8 w-8 text-muted-foreground" />}
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Cockpit Fédération</div>
            <h1 className="font-display text-2xl md:text-3xl font-bold" data-testid="fed-cockpit-name">{fed.name}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {fed.discipline && <Badge variant="outline" className="capitalize">{fed.discipline}</Badge>}
              {fed.is_copf && <Badge className="bg-[hsl(var(--aito-gold))] text-black">COPF</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/federations/${slug}`}>
            <Button size="sm" variant="outline" data-testid="fed-cockpit-public-view"><ExternalLink className="h-4 w-4 mr-1.5" />Fiche publique</Button>
          </Link>
          <Link to={`/federations/${fed.id}/edit`}>
            <Button size="sm" variant="outline" data-testid="fed-cockpit-edit"><Settings className="h-4 w-4 mr-1.5" />Modifier</Button>
          </Link>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard label="Clubs affiliés" value={kpis.clubs_count} icon={Building2} tone="lagoon" testid="fed-kpi-clubs" />
        <KpiCard
          label="Athletes du périmètre"
          value={kpis.athletes_count}
          icon={Users}
          tone="emerald"
          subtext={`${kpis.athletes_direct_count} direct · ${kpis.athletes_indirect_count} via clubs`}
          testid="fed-kpi-athletes"
        />
        <KpiCard label="Coachs" value={kpis.coaches_count} icon={Target} tone="slate" testid="fed-kpi-coaches" />
        <KpiCard label={`Résultats ${kpis.current_year}`} value={kpis.achievements_year_count} icon={TrendingUp} tone="lagoon" subtext={`${kpis.achievements_total_count} au total`} testid="fed-kpi-year-results" />
        <KpiCard label="Or" value={kpis.gold_count} icon={Medal} tone="gold" testid="fed-kpi-gold" />
        <KpiCard label="Argent" value={kpis.silver_count} icon={Medal} tone="slate" testid="fed-kpi-silver" />
        <KpiCard label="Bronze" value={kpis.bronze_count} icon={Award} tone="slate" testid="fed-kpi-bronze" />
        <KpiCard label="JP 2027 SELECTED" value={kpis.jp2027_selected_count} icon={Flag} tone="gold" testid="fed-kpi-jp2027" />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto" data-testid="fed-cockpit-tabs">
          <TabsTrigger value="dashboard" data-testid="fed-tab-dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="athletes" data-testid="fed-tab-athletes">Athletes ({kpis.athletes_count})</TabsTrigger>
          <TabsTrigger value="clubs" data-testid="fed-tab-clubs">Clubs ({kpis.clubs_count})</TabsTrigger>
          <TabsTrigger value="coaches" data-testid="fed-tab-coaches">Coachs ({kpis.coaches_count})</TabsTrigger>
          <TabsTrigger value="results" data-testid="fed-tab-results">Résultats & Palmarès</TabsTrigger>
          <TabsTrigger value="affiliations" data-testid="fed-tab-affiliations">
            <UserCheck className="h-3.5 w-3.5 mr-1" />Affiliations
          </TabsTrigger>
          <TabsTrigger value="articles" data-testid="fed-tab-articles">Actualités</TabsTrigger>
          <TabsTrigger value="memory" data-testid="fed-tab-memory"><BookOpen className="h-3.5 w-3.5 mr-1" />Livrets & Mémoires</TabsTrigger>
          <TabsTrigger value="signals" data-testid="fed-tab-signals">
            <Flag className="h-3.5 w-3.5 mr-1" />Signalements
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <FedDashboardTab slug={slug} kpis={kpis} />
        </TabsContent>
        <TabsContent value="athletes" className="mt-4">
          <FedAthletesTab slug={slug} />
        </TabsContent>
        <TabsContent value="clubs" className="mt-4">
          <FedClubsTab slug={slug} />
        </TabsContent>
        <TabsContent value="coaches" className="mt-4">
          <FedCoachesTab slug={slug} />
        </TabsContent>
        <TabsContent value="results" className="mt-4">
          <FedResultsTab slug={slug} currentYear={kpis.current_year} />
        </TabsContent>
        <TabsContent value="affiliations" className="mt-4">
          <FedAffiliationsTab slug={slug} />
        </TabsContent>
        <TabsContent value="articles" className="mt-4">
          <FedArticlesTab slug={slug} />
        </TabsContent>
        <TabsContent value="memory" className="mt-4">
          <FedMemoryTab federation={fed} />
        </TabsContent>
        <TabsContent value="signals" className="mt-4">
          <FedSignalsTab slug={slug} federationName={fed.name} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
//  Tabs
// ============================================================================
function FedDashboardTab({ slug, kpis }) {
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h2 className="font-display font-semibold mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4" />Synthèse rapide</h2>
        <div className="text-sm text-muted-foreground">
          <p>Ta fédération suit actuellement <strong>{kpis.athletes_count}</strong> athletes répartis dans <strong>{kpis.clubs_count}</strong> clubs affiliés. Cette année ({kpis.current_year}) ils ont enregistré <strong>{kpis.achievements_year_count}</strong> résultats.</p>
          {kpis.jp2027_selected_count > 0 && <p className="mt-2">🏆 <strong>{kpis.jp2027_selected_count}</strong> athlete{kpis.jp2027_selected_count > 1 ? 's' : ''} SELECTED pour les Jeux du Pacifique 2027.</p>}
        </div>
      </Card>

      {/* V11.4 — Habilitations & Guide (rendre les permissions faciles à comprendre) */}
      <Card className="p-5 border-[hsl(var(--aito-lagoon))]/40" data-testid="fed-cockpit-permissions-guide">
        <h2 className="font-display font-semibold mb-3 flex items-center gap-2 text-[hsl(var(--aito-lagoon))]">
          <Crown className="h-4 w-4" />
          Habilitations & Guide Fédération
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          Voici ce que ta fédération peut faire — et ce qui reste réservé à l'équipe AITO / COPF.
          Aucun bouton caché : cette carte explique tout ce qui est accessible depuis ton cockpit.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Ce que tu peux faire */}
          <div className="rounded border border-emerald-200 bg-emerald-50/50 p-3">
            <div className="flex items-center gap-1.5 mb-2 text-emerald-700 font-semibold text-sm">
              <CheckCircle2 className="h-4 w-4" /> Tu peux
            </div>
            <ul className="text-xs space-y-1.5 text-emerald-900">
              <li>• Consulter <strong>tes clubs affiliés</strong>, athletes et coachs de ton périmètre</li>
              <li>• Voir tous les <strong>résultats et palmarès</strong> de tes athletes</li>
              <li>• Filtrer les résultats par année, médaille, discipline</li>
              <li>• Accepter, refuser ou révoquer les <strong>demandes d'affiliation</strong> entrantes</li>
              <li>• Modifier ta <strong>fiche fédération</strong> (nom, logo, description, contacts)</li>
              <li>• Suivre les <strong>articles Curator</strong> liés à ton périmètre</li>
              <li>• <strong>Demander un livret / mémoire</strong> sur ta fédération, un club ou un athlete</li>
              <li>• Voir les <strong>KPI et statistiques</strong> agrégés (médailles, JP 2027)</li>
            </ul>
          </div>

          {/* Ce que tu ne peux pas faire */}
          <div className="rounded border border-amber-200 bg-amber-50/50 p-3">
            <div className="flex items-center gap-1.5 mb-2 text-amber-700 font-semibold text-sm">
              <XCircle className="h-4 w-4" /> Réservé Admin AITO / COPF
            </div>
            <ul className="text-xs space-y-1.5 text-amber-900">
              <li>• <strong>Créer</strong> un athlete, coach, club ou fédération dans la base AITO</li>
              <li>• <strong>Importer en masse</strong> athletes/clubs/coachs (Excel, CSV)</li>
              <li>• Modifier les <strong>données personnelles</strong> d'un athlete affilié</li>
              <li>• Créer une <strong>sélection officielle JP 2027</strong> (rôle COPF)</li>
              <li>• Valider institutionnellement des résultats (rôle COPF)</li>
              <li>• Se déclarer « <strong>COPF</strong> » ou changer le périmètre canonique</li>
              <li>• Injecter automatiquement des données depuis un import documentaire</li>
            </ul>
          </div>
        </div>

        <div className="mt-4 rounded border border-[hsl(var(--aito-lagoon))]/30 bg-[hsl(var(--aito-lagoon))]/5 p-3">
          <div className="flex items-center gap-1.5 mb-1 text-[hsl(var(--aito-lagoon))] font-semibold text-sm">
            <BookOpen className="h-4 w-4" /> Import pour livret / mémoire
          </div>
          <p className="text-xs text-muted-foreground">
            Depuis la fenêtre d'édition d'un projet de mémoire, tu peux ajouter <strong>PDF, Excel, coupures de
            presse, photos historiques</strong>. Ces fichiers alimentent uniquement le <strong>corpus documentaire</strong>{" "}
            du livret — ils ne créent jamais automatiquement d'athlete, de club ou de résultat dans la base
            AITO. Si un élément importé pourrait enrichir la base canonique, une suggestion est envoyée
            à l'admin AITO pour validation.
          </p>
        </div>

        <div className="mt-3 text-[11px] text-muted-foreground italic">
          Besoin de plus de droits ? Contacte l'équipe AITO — nous ne créons jamais de raccourci sans validation admin.
        </div>
      </Card>

      <Card className="p-5 bg-secondary/40">
        <h3 className="font-display font-semibold text-sm mb-2">Raccourcis</h3>
        <div className="flex flex-wrap gap-2 text-xs">
          <Link to={`/federations/${slug}`}><Button size="sm" variant="outline" data-testid="fed-dashboard-sc-public"><ExternalLink className="h-3 w-3 mr-1" />Voir la fiche publique</Button></Link>
          <Link to="/mes-validations"><Button size="sm" variant="outline" data-testid="fed-dashboard-sc-affil"><Users className="h-3 w-3 mr-1" />Valider affiliations entrantes</Button></Link>
          <Link to="/federation/validations"><Button size="sm" variant="outline" data-testid="fed-dashboard-sc-palmares"><ShieldCheck className="h-3 w-3 mr-1" />Validations palmarès</Button></Link>
          <Link to={`/road-2027?federation=${encodeURIComponent(slug)}`}><Button size="sm" variant="outline" data-testid="fed-dashboard-sc-jp2027"><Flag className="h-3 w-3 mr-1" />JP 2027 (lecture)</Button></Link>
          <Link to="/soutien"><Button size="sm" variant="outline" data-testid="fed-dashboard-sc-aides"><Award className="h-3 w-3 mr-1" />Aides sportives</Button></Link>
          <Link to="/mes-memoires"><Button size="sm" variant="outline" data-testid="fed-dashboard-sc-memoires"><BookOpen className="h-3 w-3 mr-1" />Mes livrets & mémoires</Button></Link>
        </div>
        <div className="text-[10px] text-muted-foreground italic mt-2">
          <strong>Rappel</strong> — Ta fédération peut lire les données JP 2027 de son périmètre.
          Les <em>sélections officielles</em> restent gérées par l'équipe COPF / Admin.
          Si tu constates une erreur, utilise l'onglet <strong>Signalements</strong>.
        </div>
      </Card>
    </div>
  );
}

function FedAthletesTab({ slug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeFilter, setScopeFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/federation-cockpit/${slug}/athletes?scope_filter=${scopeFilter}&limit=500`);
        setItems(data.items || []);
      } catch (e) { showError(e); }
      finally { setLoading(false); }
    })();
  }, [slug, scopeFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((a) => (a.full_name || "").toLowerCase().includes(s));
  }, [items, search]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={scopeFilter} onValueChange={setScopeFilter}>
          <SelectTrigger className="w-56" data-testid="fed-athletes-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Périmètre complet (direct + via club)</SelectItem>
            <SelectItem value="direct">Affiliés directement à la fédé</SelectItem>
            <SelectItem value="indirect">Via un club affilié</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-sm">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un athlète" className="pl-7 h-8" data-testid="fed-athletes-search" />
        </div>
        <div className="text-xs text-muted-foreground">{filtered.length} / {items.length}</div>
        <ExportXlsxButton
          endpoint={`/exports-xlsx/federation/${slug}/athletes`}
          params={{ scope_filter: scopeFilter }}
          filename={`AITO_${slug}_athletes.xlsx`}
          authRequired
          testid="fed-athletes-export-xlsx"
          className="ml-auto"
        />
      </div>
      {loading ? <Skeleton className="h-40 w-full" /> : filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="fed-athletes-empty">
          Aucun athlète {scopeFilter === "direct" ? "affilié directement" : scopeFilter === "indirect" ? "via un club" : ""} pour l'instant.
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((a) => (
            <Link key={a.id} to={`/athletes/${a.slug}`} className="hover:opacity-80 transition" data-testid={`fed-athlete-${a.id}`}>
              <Card className="p-3">
                <div className="aspect-square rounded overflow-hidden bg-muted mb-2">
                  <img src={a.photo_url || photoFallback(a)} alt={a.full_name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = photoFallback(a); }} />
                </div>
                <div className="text-sm font-medium truncate">{a.full_name}</div>
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                  <span className="capitalize">{a.main_discipline || "—"}</span>
                  {a._fed_scope_type === "indirect" && <Badge variant="outline" className="text-[9px] px-1">via club</Badge>}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function FedClubsTab({ slug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/federation-cockpit/${slug}/clubs`); setItems(data.items || []); }
      catch (e) { showError(e); } finally { setLoading(false); }
    })();
  }, [slug]);
  if (loading) return <Skeleton className="h-40" />;
  if (items.length === 0) return <Card className="p-8 text-center text-sm text-muted-foreground">Aucun club affilié.</Card>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((c) => (
        <Link key={c.id} to={`/clubs/${c.slug}`} data-testid={`fed-club-${c.id}`}>
          <Card className="p-3 flex items-center gap-3 hover:bg-accent transition">
            <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center shrink-0">
              {c.logo_url ? <img src={c.logo_url} alt={c.name} className="object-cover w-full h-full rounded-lg" /> : <Building2 className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{c.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{[c.commune, c.island].filter(Boolean).join(" · ") || c.discipline}</div>
            </div>
          </Card>
        </Link>
      ))}
    </div>
  );
}

function FedCoachesTab({ slug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/federation-cockpit/${slug}/coaches`); setItems(data.items || []); }
      catch (e) { showError(e); } finally { setLoading(false); }
    })();
  }, [slug]);
  if (loading) return <Skeleton className="h-40" />;
  if (items.length === 0) return <Card className="p-8 text-center text-sm text-muted-foreground">Aucun coach identifié.</Card>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((c) => (
        <Link key={c.id} to={`/coachs/${c.slug}`} data-testid={`fed-coach-${c.id}`}>
          <Card className="p-3 flex items-center gap-3 hover:bg-accent transition">
            <div className="h-10 w-10 rounded-full bg-muted overflow-hidden shrink-0">
              {c.photo_url ? <img src={c.photo_url} className="w-full h-full object-cover" alt="" /> : <Users className="h-6 w-6 text-muted-foreground m-auto" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{c.full_name}</div>
              <div className="text-[11px] text-muted-foreground truncate capitalize">{c.discipline || "—"}</div>
            </div>
          </Card>
        </Link>
      ))}
    </div>
  );
}

function FedResultsTab({ slug, currentYear }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("all");
  const [medal, setMedal] = useState("all");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = new URLSearchParams();
      if (year !== "all") params.set("year", year);
      if (medal !== "all") params.set("medal", medal);
      params.set("limit", "300");
      try { const { data } = await api.get(`/federation-cockpit/${slug}/achievements?${params}`); setItems(data.items || []); }
      catch (e) { showError(e); } finally { setLoading(false); }
    })();
  }, [slug, year, medal]);

  const years = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={String(year)} onValueChange={setYear}>
          <SelectTrigger className="w-40" data-testid="fed-results-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les années</SelectItem>
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={medal} onValueChange={setMedal}>
          <SelectTrigger className="w-40" data-testid="fed-results-medal"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes médailles</SelectItem>
            <SelectItem value="or">Or</SelectItem>
            <SelectItem value="argent">Argent</SelectItem>
            <SelectItem value="bronze">Bronze</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground">{items.length} résultats</div>
        <ExportXlsxButton
          endpoint={`/exports-xlsx/federation/${slug}/achievements`}
          params={{ year: year !== "all" ? year : undefined, medal: medal !== "all" ? medal : undefined }}
          filename={`AITO_${slug}_resultats.xlsx`}
          authRequired
          testid="fed-results-export-xlsx"
          className="ml-auto"
        />
      </div>
      {loading ? <Skeleton className="h-40" /> : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Aucun résultat correspondant.</Card>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="fed-results-table">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left">Athlete</th>
                <th className="px-3 py-2 text-left hidden md:table-cell">Compétition</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left hidden sm:table-cell">Catégorie</th>
                <th className="px-3 py-2 text-left">Résultat</th>
                <th className="px-3 py-2 text-left">Médaille</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t hover:bg-muted/30" data-testid={`fed-result-row-${r.id}`}>
                  <td className="px-3 py-2">
                    {r.athlete_slug ? (
                      <Link to={`/athletes/${r.athlete_slug}`} className="hover:underline">{r.athlete_name || "—"}</Link>
                    ) : r.athlete_name}
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell truncate max-w-[300px]">{r.competition_name || "—"}</td>
                  <td className="px-3 py-2 text-[11px] text-muted-foreground">{r.date || r.year || "—"}</td>
                  <td className="px-3 py-2 hidden sm:table-cell text-xs">{r.category || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.rank || r.result || "—"}</td>
                  <td className="px-3 py-2">
                    {r.medal ? (
                      <Badge className={
                        r.medal === "or" ? "bg-[hsl(var(--aito-gold))] text-black" :
                        r.medal === "argent" ? "bg-slate-300 text-black" :
                        r.medal === "bronze" ? "bg-orange-300 text-black" : ""
                      }>{r.medal}</Badge>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const CONTENT_STATUS_TABS = [
  { v: "inbox", label: "À traiter" },
  { v: "validated", label: "Validés" },
  { v: "published", label: "Publiés" },
  { v: "rejected", label: "Refusés" },
];

const CATEGORY_LABELS = {
  communique_federation: "Communiqué fédération",
  communique_club: "Communiqué club",
  resultats: "Résultats",
  retour_competition: "Retour compétition",
  competition_a_venir: "Compétition à venir",
  portrait: "Portrait",
  interview: "Interview",
  billet_humeur: "Billet d'humeur",
  soiree: "Soirée",
  galerie: "Galerie",
  video: "Vidéo",
  actualite: "Actualité",
  autre: "Autre",
};

function FedArticlesTab({ slug }) {
  const [tab, setTab] = useState("inbox");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [publishable, setPublishable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/federation-cockpit/${slug}/contents?status=${tab}&limit=100`);
      setItems(data.items || []);
      setCounts(data.counts || {});
      setPublishable(data.publishable_categories || []);
    } catch (e) { showError(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, tab]);

  const act = async (item, kind) => {
    setBusy((b) => ({ ...b, [item.id]: kind }));
    try {
      if (kind === "publish") {
        await api.post(`/federation-cockpit/${slug}/contents/${item.id}/publish`, {});
        toast.success("Contenu publié au titre de ta fédération.");
      } else if (kind === "reject") {
        const note = window.prompt("Motif de refus (visible en interne) :", "Contenu hors périmètre.");
        if (note === null) { setBusy((b) => { const n = { ...b }; delete n[item.id]; return n; }); return; }
        await api.post(`/federation-cockpit/${slug}/contents/${item.id}/reject`, { note });
        toast.success("Contenu refusé.");
      }
      await load();
    } catch (e) {
      showError(e, { prefix: "Action contenu :" });
    } finally { setBusy((b) => { const n = { ...b }; delete n[item.id]; return n; }); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-wrap gap-1.5">
          {CONTENT_STATUS_TABS.map((t) => (
            <Button
              key={t.v}
              size="sm"
              variant={tab === t.v ? "default" : "outline"}
              onClick={() => setTab(t.v)}
              data-testid={`fed-contents-tab-${t.v}`}
              className={tab === t.v ? "bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" : ""}
            >
              {t.label}
              {counts[t.v] > 0 && (
                <Badge className="ml-1.5 bg-white/70 text-black text-[10px]">{counts[t.v]}</Badge>
              )}
            </Button>
          ))}
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="fed-contents-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />Rafraîchir
        </Button>
      </div>

      <div className="text-[11px] text-muted-foreground bg-secondary/40 border border-dashed rounded-md p-2">
        <strong>Publication directe autorisée</strong> pour les catégories :{" "}
        {publishable.map((c) => CATEGORY_LABELS[c] || c).join(", ") || "—"}.
        Les autres catégories sont modérées par l'équipe COPF / Admin.
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground border-dashed" data-testid="fed-contents-empty">
          Aucun contenu {CONTENT_STATUS_TABS.find((t) => t.v === tab)?.label.toLowerCase()} sur ton périmètre.
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fed-contents-grid">
          {items.map((it) => {
            const canPublish = it._fed_can_publish;
            const cat = CATEGORY_LABELS[it.category] || it.category || "—";
            return (
              <Card key={it.id} className="p-3 flex flex-col gap-2" data-testid={`fed-content-${it.id}`}>
                {it.image_url && (
                  <div className="aspect-video rounded overflow-hidden bg-muted">
                    <img src={it.image_url} className="w-full h-full object-cover" alt="" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                  </div>
                )}
                <div className="flex items-center justify-between gap-1 flex-wrap">
                  <Badge variant="outline" className="text-[10px]">{cat}</Badge>
                  {canPublish && <Badge className="bg-emerald-600 text-white text-[10px]">Publiable</Badge>}
                  {!canPublish && it.category && !publishable.includes(it.category) && (
                    <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">Modération COPF</Badge>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{it.source_name || "—"}</div>
                <a href={it.url || it.source_url} target="_blank" rel="noreferrer"
                   className="text-sm font-medium hover:text-[hsl(var(--aito-lagoon))] line-clamp-2 block">
                  {it.title || "Sans titre"}
                </a>
                {it.summary && <p className="text-xs text-muted-foreground line-clamp-2">{it.summary}</p>}
                {tab !== "published" && tab !== "rejected" && (
                  <div className="flex gap-1.5 mt-1">
                    {canPublish && (
                      <Button
                        size="sm"
                        onClick={() => act(it, "publish")}
                        disabled={!!busy[it.id]}
                        className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`fed-content-publish-${it.id}`}
                      >
                        {busy[it.id] === "publish" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                        Publier
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act(it, "reject")}
                      disabled={!!busy[it.id]}
                      className="border-orange-400 text-orange-700 hover:bg-orange-50"
                      data-testid={`fed-content-reject-${it.id}`}
                    >
                      {busy[it.id] === "reject" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
                      Refuser
                    </Button>
                  </div>
                )}
                {tab === "published" && it.published_by_role === "federation" && (
                  <div className="text-[10px] text-emerald-700">
                    ✓ Publié au titre de {it.published_by_federation_name || "ta fédération"}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FedMemoryTab({ federation }) {
  return (
    <div className="space-y-3">
      <Card className="p-5">
        <h2 className="font-display font-semibold mb-2 flex items-center gap-2"><BookOpen className="h-4 w-4" />Livrets & Mémoires — {federation.name}</h2>
        <p className="text-sm text-muted-foreground mb-3">
          Créer un livret ou une mémoire sportive sur ton périmètre : la fédération, un club affilié, un athlete du périmètre, ou une période. L'IA compose automatiquement le corpus depuis les données AITO canoniques et tes documents complémentaires.
        </p>
        <div className="flex flex-wrap gap-2">
          <Link to={`/memoires/nouvelle-demande?entity_type=federation&entity_id=${federation.id}`}>
            <Button size="sm" data-testid="fed-memory-create-federation" className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(var(--aito-gold))]/85">
              <BookOpen className="h-3.5 w-3.5 mr-1.5" />Mémoire de la Fédération
            </Button>
          </Link>
          <Link to="/mes-memoires">
            <Button size="sm" variant="outline"><ExternalLink className="h-3.5 w-3.5 mr-1.5" />Tous mes livrets</Button>
          </Link>
        </div>
        <p className="text-[11px] text-muted-foreground italic mt-3">
          Pour créer un livret sur un club ou un athlete de ton périmètre : ouvre la fiche correspondante et clique « Créer un livret ».
        </p>
      </Card>
    </div>
  );
}

// ============================================================================
//  V13.16 — Onglet Affiliations (accept/refuse/revoke)
// ============================================================================
const AFFIL_STATUS = [
  { v: "pending", label: "En attente" },
  { v: "accepted", label: "Acceptées" },
  { v: "rejected", label: "Refusées" },
  { v: "revoked", label: "Révoquées" },
  { v: "all", label: "Toutes" },
];

const ROLE_LABEL = {
  athlete_of_federation: "Athlète → Fédération",
  athlete_of_club: "Athlète → Club",
  club_of_federation: "Club → Fédération",
  coach_of_club: "Coach → Club",
  athlete_coached_by: "Athlète coaché·e par",
};

function FedAffiliationsTab({ slug }) {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // { [link_id]: "accept"|"reject"|"revoke" }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/federation-cockpit/${slug}/affiliations?status=${status}`);
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) { showError(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, status]);

  const act = async (link, kind) => {
    setBusy((b) => ({ ...b, [link.id]: kind }));
    try {
      if (kind === "accept" || kind === "reject") {
        await api.patch(`/entity-links/${link.id}`, { action: kind });
        toast.success(kind === "accept" ? "Affiliation acceptée." : "Affiliation refusée.");
      } else if (kind === "revoke") {
        await api.delete(`/entity-links/${link.id}`);
        toast.success("Affiliation révoquée.");
      }
      await load();
    } catch (e) {
      showError(e, { prefix: "Action affiliation :" });
    } finally { setBusy((b) => { const n = { ...b }; delete n[link.id]; return n; }); }
  };

  const StatusBadge = ({ st }) => {
    if (st === "pending" || st === "declared") return <Badge className="bg-amber-500 text-white text-[10px]">En attente</Badge>;
    if (st === "accepted") return <Badge className="bg-emerald-600 text-white text-[10px]"><CheckCircle2 className="h-3 w-3 mr-0.5" />Accepté</Badge>;
    if (st === "rejected") return <Badge className="bg-slate-500 text-white text-[10px]"><XCircle className="h-3 w-3 mr-0.5" />Refusé</Badge>;
    if (st === "revoked") return <Badge variant="outline" className="text-[10px]">Révoqué</Badge>;
    return <Badge variant="outline" className="text-[10px]">{st}</Badge>;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {AFFIL_STATUS.map((s) => (
            <Button
              key={s.v}
              size="sm"
              variant={status === s.v ? "default" : "outline"}
              onClick={() => setStatus(s.v)}
              data-testid={`fed-affil-filter-${s.v}`}
              className={status === s.v ? "bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" : ""}
            >
              {s.label}
              {counts[s.v] > 0 && s.v !== "all" && (
                <Badge className="ml-1.5 bg-white/70 text-black text-[10px]">{counts[s.v]}</Badge>
              )}
            </Button>
          ))}
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="fed-affil-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />Rafraîchir
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="fed-affil-empty">
          <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
          Aucune affiliation {status !== "all" ? AFFIL_STATUS.find((s) => s.v === status)?.label.toLowerCase() : ""} pour l'instant.
        </Card>
      ) : (
        <Card className="divide-y" data-testid="fed-affil-list">
          {items.map((l) => (
            <div key={l.id} className="flex items-center gap-3 p-3 hover:bg-muted/30 transition" data-testid={`fed-affil-row-${l.id}`}>
              <div className="h-10 w-10 rounded-full bg-muted overflow-hidden shrink-0 border">
                {l.source_photo
                  ? <img src={l.source_photo} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Users className="h-4 w-4" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {l.source_slug ? (
                    <Link to={`/${l.source_type === "club" ? "clubs" : l.source_type === "coach" ? "coachs" : "athletes"}/${l.source_slug}`}
                      target="_blank" rel="noreferrer" className="font-medium hover:underline">
                      {l.source_name || l.source_id}
                    </Link>
                  ) : <span className="font-medium">{l.source_name || l.source_id}</span>}
                  <span className="text-[11px] text-muted-foreground">{ROLE_LABEL[l.role] || l.role}</span>
                  {l.target_type === "club" && l.target_name && (
                    <span className="text-[11px] text-muted-foreground">→ {l.target_name}</span>
                  )}
                  <StatusBadge st={l.status} />
                </div>
                <div className="text-[11px] text-muted-foreground flex gap-2 flex-wrap mt-0.5">
                  {l.source_discipline && <span className="capitalize">{l.source_discipline}</span>}
                  {l.source_locality && <span>· {l.source_locality}</span>}
                  {l.declared_at && <span>· Déclaré le {new Date(l.declared_at).toLocaleDateString("fr-FR")}</span>}
                  {l.review_note && <span>· « {l.review_note} »</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {(l.status === "pending" || l.status === "declared") && (
                  <>
                    <Button size="sm" onClick={() => act(l, "accept")}
                      disabled={!!busy[l.id]}
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid={`fed-affil-accept-${l.id}`}>
                      {busy[l.id] === "accept" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                      Accepter
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => act(l, "reject")}
                      disabled={!!busy[l.id]}
                      className="border-orange-400 text-orange-700 hover:bg-orange-50"
                      data-testid={`fed-affil-reject-${l.id}`}>
                      {busy[l.id] === "reject" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
                      Refuser
                    </Button>
                  </>
                )}
                {l.status === "accepted" && (
                  <Button size="sm" variant="outline" onClick={() => act(l, "revoke")}
                    disabled={!!busy[l.id]}
                    className="border-slate-400"
                    data-testid={`fed-affil-revoke-${l.id}`}>
                    {busy[l.id] === "revoke" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
                    Révoquer
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ============================================================================
//  V13.16 — Onglet Signalements / Propositions
//  (create + list — API à faire en Phase 2.3 backend)
// ============================================================================
function FedSignalsTab({ slug, federationName }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ kind: "palmares_error", target_label: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/federation-cockpit/${slug}/signals`);
      setItems(data.items || []);
    } catch (e) {
      // Endpoint peut ne pas être encore implémenté — tolérer silencieusement.
      if (e?.response?.status !== 404) showError(e);
      setItems([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async () => {
    if (!form.notes.trim()) { toast.error("Décris précisément le signalement."); return; }
    setSubmitting(true);
    try {
      await api.post(`/federation-cockpit/${slug}/signals`, {
        kind: form.kind,
        target_label: form.target_label.trim() || null,
        notes: form.notes.trim(),
      });
      toast.success("Signalement envoyé à l'équipe COPF / Admin.");
      setForm({ kind: "palmares_error", target_label: "", notes: "" });
      await load();
    } catch (e) { showError(e, { prefix: "Signalement :" }); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 md:p-5" data-testid="fed-signals-form">
        <h2 className="font-display font-semibold text-sm mb-1 flex items-center gap-2">
          <MessageSquareWarning className="h-4 w-4 text-[hsl(var(--aito-energy))]" />
          Signaler ou proposer une correction
        </h2>
        <p className="text-xs text-muted-foreground mb-3">
          Pour <strong>{federationName}</strong> — La fédération peut signaler mais ne peut pas valider institutionnellement.
          Un membre de l'équipe COPF / Admin traitera ta demande.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <select
            className="border rounded-md px-2 py-2 text-sm bg-background focus-ring"
            value={form.kind}
            onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
            data-testid="fed-signals-kind"
          >
            <option value="palmares_error">Erreur de palmarès / résultat</option>
            <option value="duplicate">Doublon athlète suspect</option>
            <option value="claim">Revendication à vérifier</option>
            <option value="jp2027_error">Erreur de sélection JP 2027</option>
            <option value="other">Autre proposition</option>
          </select>
          <Input
            placeholder="Cible concernée (nom athlète, résultat, etc.)"
            value={form.target_label}
            onChange={(e) => setForm((f) => ({ ...f, target_label: e.target.value }))}
            data-testid="fed-signals-target"
          />
          <Button
            onClick={submit}
            disabled={submitting || !form.notes.trim()}
            className="bg-[hsl(var(--aito-energy))] text-white hover:bg-[hsl(var(--aito-energy))]/85"
            data-testid="fed-signals-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <AlertTriangle className="h-4 w-4 mr-1.5" />}
            Envoyer
          </Button>
        </div>
        <textarea
          rows={3}
          className="w-full border rounded-md px-3 py-2 mt-2 text-sm bg-background focus-ring"
          placeholder="Décris précisément ce que tu veux signaler (contexte, athlète, date, source…)."
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          data-testid="fed-signals-notes"
        />
      </Card>

      <div>
        <h3 className="font-display font-semibold text-sm mb-2">Mes signalements récents</h3>
        {loading ? (
          <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : items.length === 0 ? (
          <Card className="p-6 text-center text-sm text-muted-foreground border-dashed" data-testid="fed-signals-empty">
            Aucun signalement pour l'instant.
          </Card>
        ) : (
          <Card className="divide-y" data-testid="fed-signals-list">
            {items.map((it) => (
              <div key={it.id} className="p-3 flex items-start gap-3" data-testid={`fed-signal-row-${it.id}`}>
                <AlertTriangle className="h-4 w-4 mt-0.5 text-[hsl(var(--aito-energy))] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">{it.kind}</span>
                    <Badge variant="outline" className="text-[10px]">{it.status}</Badge>
                    {it.target_label && <span className="text-xs">· {it.target_label}</span>}
                  </div>
                  <p className="text-sm mt-1 line-clamp-2">{it.notes}</p>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {it.created_at && new Date(it.created_at).toLocaleString("fr-FR")}
                    {it.resolution_note && <> · Réponse admin : « {it.resolution_note} »</>}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

